import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FirestoreError } from 'firebase/firestore';
import type {
  CatalogDesignation,
  Client,
  Designation,
  Project,
  Tag,
  TimeEntry,
  Timesheet,
  User,
} from '@altyor-kronos/shared-types';
import { markAndSortTrackerProjects, type TrackerProject } from './trackerProjects';
import { usePolledResource } from '../../core/data/usePolledResource';
import { apiGet, apiList, ApiError } from '../../core/data/restClient';
import { onMutation } from '../../core/data/mutationSignal';

// A REST-serialised Firestore Timestamp arrives as {_seconds,_nanoseconds} (admin
// SDK) — convert to ms for use as a pagination cursor. Tolerates a live Timestamp too.
function tsToMillis(t: unknown): number {
  const o = t as { _seconds?: number; _nanoseconds?: number; seconds?: number; toMillis?: () => number };
  if (o && typeof o.toMillis === 'function') return o.toMillis();
  const s = o?._seconds ?? o?.seconds ?? 0;
  const n = o?._nanoseconds ?? 0;
  return s * 1000 + Math.floor(n / 1e6);
}

// ─── Projects the current user can log time against ─────────────────

export interface TrackerProjectsState {
  projects: TrackerProject[];
  loading: boolean;
  error: FirestoreError | null;
}

/**
 * Lists active/on-hold projects and flags each with the viewer's
 * assignment (`assigned`, via `markAndSortTrackerProjects`), assigned
 * projects first.
 *
 * The tracker lists EVERY loggable project: an entry on a non-member
 * project auto-adds the user to the team server-side and notifies the R&D
 * managers + project managers (functions/on-time-entry-created.ts).
 */
export function useTrackerProjects(user: User | null): TrackerProjectsState {
  const { rows, loading, error } = usePolledResource<Project & { id: string }>(
    'tracker-projects',
    () => apiList<Project & { id: string }>('/projects'),
  );
  const flagged = useMemo(
    () => markAndSortTrackerProjects(rows.map((r) => ({ id: r.id, data: r })), user),
    [rows, user],
  );
  return { projects: flagged, loading, error: error as FirestoreError | null };
}

// ─── Designations for a single project ──────────────────────────────

export interface ProjectDesignationsState {
  designations: Array<{ id: string; data: Designation }>;
  loading: boolean;
  error: FirestoreError | null;
}

/**
 * Read a single project's designations subcollection.
 * Empty projectId is allowed and returns an idle empty state —
 * convenient for the tracker form when no project is selected yet.
 */
export function useProjectDesignations(projectId: string): ProjectDesignationsState {
  const { rows, loading, error } = usePolledResource<Designation & { id: string }>(
    projectId || null,
    () => apiList<Designation & { id: string }>(`/projects/${projectId}/designations`),
  );
  return {
    designations: rows.map((r) => ({ id: r.id, data: r })),
    loading,
    error: error as FirestoreError | null,
  };
}

/**
 * Read designations across multiple projects and return a merged
 * id → name map. Safe for any user because designations are only
 * readable for projects the user is assigned to (and entries in the
 * timesheet can only reference assigned projects).
 *
 * Re-fetches when `projectIds` changes (by serialised key).
 */
export function useMultiProjectDesignations(
  projectIds: readonly string[],
  opts?: { excludeArchived?: boolean },
): Map<string, string> {
  const key = projectIds.length ? projectIds.slice().sort().join(',') : null;
  const { rows } = usePolledResource<Designation & { id: string }>(key, async () => {
    const per = await Promise.all(
      projectIds.map((pid) =>
        apiList<Designation & { id: string }>(`/projects/${pid}/designations`),
      ),
    );
    return per.flat();
  });
  // Callers that *label past entries* (tracker week, history, timesheet)
  // need archived designations in the map or old rows lose their name.
  // Callers that *offer a shortcut* (the calendar's recent pairs) opt out:
  // buildRecentPairOptions already skips a pair whose label is unknown.
  const excludeArchived = opts?.excludeArchived ?? false;
  return useMemo(
    () =>
      new Map(
        rows.filter((r) => !(excludeArchived && r.isArchived)).map((r) => [r.id, r.name]),
      ),
    [rows, excludeArchived],
  );
}

// ─── TimeEntry subscriptions ────────────────────────────────────────

export interface TimeEntriesState {
  entries: Array<{ id: string; data: TimeEntry }>;
  loading: boolean;
  error: FirestoreError | null;
}

/**
 * Read **the current user's** entries starting from `sinceStartOfDay`.
 * The server scopes this to the owner; safe for every role.
 */
export function useMyRecentEntries(
  userId: string,
  sinceStartOfDay: Date,
): TimeEntriesState {
  const from = sinceStartOfDay.getTime();
  const key = userId ? `${userId}:${from}` : null;
  const { rows, loading, error } = usePolledResource<TimeEntry & { id: string }>(
    key,
    () => apiList<TimeEntry & { id: string }>('/timeEntries', { userId, from }),
  );
  return {
    entries: rows.map((r) => ({ id: r.id, data: r })),
    loading,
    error: error as FirestoreError | null,
  };
}

// ─── Full designation catalog ───────────────────────────────────────

export interface CatalogState {
  entries: Array<{ id: string; data: CatalogDesignation }>;
  loading: boolean;
  error: FirestoreError | null;
}

/**
 * Read **all** non-archived catalog entries (every section).
 *
 * Used by the Kronos tracker's hybrid picker so the user can log time
 * against any standard designation without first attaching it to the
 * project. Differs from `features/projects/useCatalogSnapshot` which
 * filters by section — the tracker needs the full catalogue at once.
 */
export function useAllCatalog(): CatalogState {
  const { rows, loading, error } = usePolledResource<CatalogDesignation & { id: string }>(
    'all-catalog',
    () => apiList<CatalogDesignation & { id: string }>('/designationCatalog'),
  );
  return {
    entries: rows.map((r) => ({ id: r.id, data: r })).filter((e) => !e.data.isArchived),
    loading,
    error: error as FirestoreError | null,
  };
}

// ─── Dashboard subscriptions (Phase 7) ─────────────────────────────

/**
 * Cross-user entries from `sinceStartOfPeriod` forward — intended
 * for the Kronos Dashboard's team overview.
 *
 * **Only safe to call for `rd_manager`.** Pass `enabled` so non-manager
 * users render the dashboard without triggering a permission error.
 */
export function useTeamRecentEntries(
  sinceStartOfPeriod: Date,
  enabled: boolean,
): TimeEntriesState {
  const from = sinceStartOfPeriod.getTime();
  // Delta comme le Rapport : un retour sur l'onglet relisait toutes les saisies
  // de l'équipe (#66). Fenêtre d'une semaine, le seul appelant (Dashboard)
  // passe un lundi.
  const fetcher = useMemo(() => makeRangeDeltaFetcher(from, from + 7 * 86_400_000 - 1), [from]);
  const { rows, loading, error } = usePolledResource<TimeEntry & { id: string }>(
    enabled ? `team:${from}` : null,
    fetcher,
  );
  return {
    entries: rows.map((r) => ({ id: r.id, data: r })),
    loading,
    error: error as FirestoreError | null,
  };
}

export interface AllDesignationsState {
  designations: Array<{ id: string; projectId: string; data: Designation }>;
  loading: boolean;
  error: FirestoreError | null;
}

/**
 * Collection-group read of every project's `designations` subcollection —
 * feeds the dashboard's "projects lagging vs hoursSold" widget.
 *
 * **Only safe to call for `rd_manager`** (and `direction`, `finance`,
 * `admin`). Passing `enabled=false` for any other viewer avoids a
 * rejected query. The server returns each row with its parent projectId.
 */
export function useAllDesignations(enabled: boolean): AllDesignationsState {
  // Collection-group list: server returns each row with its parent projectId.
  const { rows, loading, error } = usePolledResource<Designation & { id: string; projectId: string }>(
    enabled ? 'all-designations' : null,
    () => apiList<Designation & { id: string; projectId: string }>('/designations'),
  );
  return {
    designations: rows.map((r) => ({ id: r.id, projectId: r.projectId, data: r })),
    loading,
    error: error as FirestoreError | null,
  };
}

/**
 * Read `users/` for the team-member list's avatar + display name +
 * department labels. Only safe for `rd_manager` (and the other
 * privileged roles — direction/admin/finance). Pass `enabled=false`
 * for any other viewer.
 */
interface AllUsersState {
  users: Array<{ id: string; data: User }>;
  loading: boolean;
  error: FirestoreError | null;
}

export function useAllUsers(enabled: boolean): AllUsersState {
  const { rows, loading, error } = usePolledResource<User & { id: string }>(
    enabled ? 'all-users' : null,
    () => apiList<User & { id: string }>('/users'),
  );
  return {
    users: rows.map((r) => ({ id: r.id, data: r })),
    loading,
    error: error as FirestoreError | null,
  };
}

/**
 * Clients read. Everyone can list them, no role gate needed. Used to
 * resolve the dashboard's "Top Client" KPI and any future per-client
 * grouping.
 */
interface AllClientsState {
  clients: Map<string, Client>;
  loading: boolean;
  error: FirestoreError | null;
}

export function useAllClients(): AllClientsState {
  const { rows, loading, error } = usePolledResource<Client & { id: string }>(
    'all-clients',
    () => apiList<Client & { id: string }>('/clients'),
  );
  const clients = useMemo(() => new Map(rows.map((r) => [r.id, r as Client])), [rows]);
  return { clients, loading, error: error as FirestoreError | null };
}

/**
 * Lightweight Project read for the dashboard's label lookup.
 * Projects are world-readable — no scoping needed.
 */
interface AllProjectsState {
  projects: Array<{ id: string; data: Project }>;
  loading: boolean;
  error: FirestoreError | null;
}

export function useAllProjects(): AllProjectsState {
  const { rows, loading, error } = usePolledResource<Project & { id: string }>(
    'all-projects',
    () => apiList<Project & { id: string }>('/projects'),
  );
  return {
    projects: rows.map((r) => ({ id: r.id, data: r })),
    loading,
    error: error as FirestoreError | null,
  };
}

// ─── Tag catalogue ──────────────────────────────────────────────────

/**
 * Read the curated tag catalogue (`tags/`), returning the active
 * (non-archived) labels sorted alphabetically. World-readable, so
 * safe for every viewer — feeds the tracker tag pickers and the report
 * tag filter. Admins curate the list in /settings → Tags.
 */
export function useActiveTags(): string[] {
  const { rows } = usePolledResource<Tag>('active-tags', () => apiList<Tag>('/tags'));
  return useMemo(
    () => rows.filter((t) => !t.isArchived).map((t) => t.label).sort((a, b) => a.localeCompare(b)),
    [rows],
  );
}

// ─── Timesheet subscriptions (Phase 8) ──────────────────────────────

export interface TimesheetState {
  timesheet: Timesheet | null;
  loading: boolean;
  error: FirestoreError | null;
}

/**
 * Read a single weekly timesheet doc
 * (`timesheets/{userId}_{YYYY-MM-DD}`). Returns `null` when the doc
 * does not exist yet — the weekly view renders anyway and only writes
 * the doc on first submit. Empty `timesheetId` yields an idle state.
 */
export function useWeekTimesheet(timesheetId: string): TimesheetState {
  // Single-doc read via the list primitive: fetch returns [doc] or [] (404).
  const { rows, loading, error } = usePolledResource<Timesheet & { id: string }>(
    timesheetId || null,
    async () => {
      try {
        return [await apiGet<Timesheet & { id: string }>(`/timesheets/${timesheetId}`)];
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return [];
        throw e;
      }
    },
  );
  return { timesheet: rows[0] ?? null, loading, error: error as FirestoreError | null };
}

/**
 * Read the current user's entries within a [from, to] window.
 * `userId` filter keeps the query within the owner scope so it is safe
 * for every role. Used by TimesheetView for the weekly breakdown.
 */
export function useMyEntriesInWindow(
  userId: string,
  from: Date,
  to: Date,
): TimeEntriesState {
  const fromMs = from.getTime();
  const toMs = to.getTime();
  const { rows, loading, error } = usePolledResource<TimeEntry & { id: string }>(
    userId ? `${userId}:${fromMs}:${toMs}` : null,
    () => apiList<TimeEntry & { id: string }>('/timeEntries', { userId, from: fromMs, to: toMs }),
  );
  // Server returns startTime DESC; this hook's SDK contract is ASC.
  return {
    entries: rows.slice().reverse().map((r) => ({ id: r.id, data: r })),
    loading,
    error: error as FirestoreError | null,
  };
}

type EntryRow = TimeEntry & { id: string };

// Marge sur l'horloge du poste : `updatedAt` est posé par le serveur.
const DELTA_SKEW_MS = 10 * 60_000;

/**
 * Fetcher du Rapport : lecture complète la première fois, puis delta.
 *
 * « À ce jour » relit ~48 000 saisies, et usePolledResource relance à chaque
 * retour sur l'onglet et à chaque écriture — une correction de Damien coûtait
 * 48 000 lectures (issue #63). Ensuite on ne relit que ce qui a bougé
 * (`updatedSince`) + un count() de la période (1 lecture / 1 000 saisies).
 * Le delta ne voit pas les suppressions : si le compte ne colle plus, on
 * recharge tout.
 *
 * ponytail: horloge du poste à DELTA_SKEW_MS près ; un poste plus en avance
 * que ça raterait des modifications jusqu'au prochain rechargement complet.
 */
export function makeRangeDeltaFetcher(fromMs: number, toMs: number): () => Promise<EntryRow[]> {
  let rows: Map<string, EntryRow> | null = null;
  let since = 0;
  const sorted = () =>
    [...rows!.values()].sort((a, b) => tsToMillis(b.startTime) - tsToMillis(a.startTime));

  return async () => {
    const startedAt = Date.now();
    if (rows) {
      const range = new URLSearchParams({ from: String(fromMs), to: String(toMs), count: '1' });
      const [changed, { count }] = await Promise.all([
        apiList<EntryRow>('/timeEntries', { updatedSince: since }),
        apiGet<{ count: number }>(`/timeEntries?${range}`),
      ]);
      for (const r of changed) {
        const t = tsToMillis(r.startTime);
        if (t >= fromMs && t <= toMs) rows.set(r.id, r);
        else rows.delete(r.id); // déplacée hors de la période
      }
      if (rows.size === count) {
        since = startedAt - DELTA_SKEW_MS;
        return sorted();
      }
    }
    const all = await apiList<EntryRow>('/timeEntries', { from: fromMs, to: toMs });
    rows = new Map(all.map((r) => [r.id, r]));
    since = startedAt - DELTA_SKEW_MS;
    return all;
  };
}

/**
 * Admin-scoped entries within an explicit [from, to] date window.
 *
 * **Only safe for `admin`.** No `userId` clause. Pass `enabled=false`
 * for any non-admin viewer to avoid a permission rejection.
 */
export function useAdminEntriesInRange(
  from: Date,
  to: Date,
  enabled: boolean,
): TimeEntriesState {
  const fromMs = from.getTime();
  const toMs = to.getTime();
  const fetcher = useMemo(() => makeRangeDeltaFetcher(fromMs, toMs), [fromMs, toMs]);
  const { rows, loading, error } = usePolledResource<TimeEntry & { id: string }>(
    enabled ? `admin:${fromMs}:${toMs}` : null,
    fetcher,
  );
  return {
    entries: rows.map((r) => ({ id: r.id, data: r })),
    loading,
    error: error as FirestoreError | null,
  };
}

/**
 * Read entries for a single project.
 *
 * Pass `userIdScope = uid` for normal employees / managers,
 * `userIdScope = null` for `rd_manager` (no extra scope).
 */
export function useProjectEntries(
  projectId: string,
  userIdScope: string | null,
): TimeEntriesState {
  const { rows, loading, error } = usePolledResource<TimeEntry & { id: string }>(
    projectId ? `${projectId}:${userIdScope ?? ''}` : null,
    () =>
      apiList<TimeEntry & { id: string }>('/timeEntries', {
        projectId,
        userId: userIdScope ?? undefined,
      }),
  );
  return {
    entries: rows.map((r) => ({ id: r.id, data: r })),
    loading,
    error: error as FirestoreError | null,
  };
}

// ─── Paginated project entries (infinite scroll) ─────────────────────

export interface ProjectEntriesPagedState {
  entries: Array<{ id: string; data: TimeEntry }>;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: FirestoreError | null;
  loadMore: () => void;
}

/**
 * Cursor-based paginated version of `useProjectEntries`.
 *
 * Loads `pageSize` entries on mount (ordered by `startTime DESC`), then
 * appends the next page each time `loadMore()` is called. Each page costs
 * exactly `pageSize` reads — no re-reading of earlier pages.
 *
 * The same `userIdScope` rule applies as `useProjectEntries` — pass the
 * viewer's uid for non-rd_manager roles.
 */
export function useProjectEntriesPaged(
  projectId: string,
  userIdScope: string | null,
  pageSize = 10,
): ProjectEntriesPagedState {
  const [entries, setEntries] = useState<Array<{ id: string; data: TimeEntry }>>([]);
  const [loading, setLoading] = useState(Boolean(projectId));
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<FirestoreError | null>(null);
  const cursorRef = useRef<number | null>(null);
  const inFlightRef = useRef(false);

  const fetchPage = useCallback(
    // `limit` defaults to one page (+1 to detect a next page without a count
    // query); the mutation refetch below overrides it to re-read every page
    // already on screen in a single request.
    (before?: number, limit = pageSize + 1) =>
      apiList<TimeEntry & { id: string }>('/timeEntries', {
        projectId,
        userId: userIdScope ?? undefined,
        limit,
        before,
      }),
    [projectId, userIdScope, pageSize],
  );

  useEffect(() => {
    if (!projectId) {
      setEntries([]); setLoading(false); setHasMore(false); cursorRef.current = null;
      return;
    }
    let cancelled = false;
    setLoading(true); setEntries([]); setHasMore(false); cursorRef.current = null; inFlightRef.current = false;
    fetchPage()
      .then((rows) => {
        if (cancelled) return;
        const page = rows.slice(0, pageSize);
        cursorRef.current = page.length ? tsToMillis(page[page.length - 1].startTime) : null;
        setHasMore(rows.length > pageSize);
        setEntries(page.map((r) => ({ id: r.id, data: r })));
        setLoading(false);
      })
      .catch((e) => { if (!cancelled) { setError(e as FirestoreError); setLoading(false); } });
    return () => { cancelled = true; };
  }, [projectId, userIdScope, pageSize, fetchPage]);

  // Cursor pagination accumulates pages in local state, so unlike the polled
  // hooks this list had NO refresh path at all: an edit or delete stayed on
  // screen until a page reload. Re-read exactly the window already loaded —
  // refetching only page 1 would throw the user back to the top.
  const loadedCountRef = useRef(0);
  loadedCountRef.current = entries.length;
  useEffect(() => {
    if (!projectId) return;
    return onMutation(() => {
      const size = loadedCountRef.current;
      if (!size) return;
      fetchPage(undefined, size + 1)
        .then((rows) => {
          const page = rows.slice(0, size);
          cursorRef.current = page.length ? tsToMillis(page[page.length - 1].startTime) : null;
          setHasMore(rows.length > size);
          setEntries(page.map((r) => ({ id: r.id, data: r })));
        })
        .catch((e) => setError(e as FirestoreError));
    });
  }, [projectId, fetchPage]);

  const loadMore = useCallback(() => {
    if (inFlightRef.current || !hasMore || cursorRef.current == null) return;
    inFlightRef.current = true;
    setLoadingMore(true);
    // ponytail: ms cursor (not a snapshot); rows sharing the exact boundary
    // startTime can be dropped — acceptable for a history browse.
    fetchPage(cursorRef.current)
      .then((rows) => {
        const page = rows.slice(0, pageSize);
        if (page.length) cursorRef.current = tsToMillis(page[page.length - 1].startTime);
        setHasMore(rows.length > pageSize);
        setEntries((prev) => [...prev, ...page.map((r) => ({ id: r.id, data: r }))]);
        setLoadingMore(false); inFlightRef.current = false;
      })
      .catch((e) => { setError(e as FirestoreError); setLoadingMore(false); inFlightRef.current = false; });
  }, [hasMore, fetchPage, pageSize]);

  return { entries, loading, loadingMore, hasMore, error, loadMore };
}

// ─── Paginated history (weeks before the current one) ────────────────

export interface HistoryWeeksState {
  entries: Array<{ id: string; data: TimeEntry }>;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: FirestoreError | null;
  loadMore: () => void;
}

/**
 * One-shot, cursor-paginated read of the current user's entries that
 * started BEFORE `currentWeekStart` (i.e. everything older than the
 * week shown by `useMyRecentEntries`).
 *
 * Each page covers roughly `weeksPerPage` weeks. `loadMore()` fetches the
 * next slice continuing from the last document. Satisfies the timeEntries
 * owner scope via the `userId` filter.
 */
export function useMyHistoryWeeks(
  userId: string,
  currentWeekStart: Date,
  weeksPerPage = 8,
): HistoryWeeksState {
  const [entries, setEntries] = useState<Array<{ id: string; data: TimeEntry }>>([]);
  const [loading, setLoading] = useState(Boolean(userId));
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<FirestoreError | null>(null);
  const cursorRef = useRef<number | null>(null);
  const inFlightRef = useRef(false);

  const pageSize = weeksPerPage * 25;
  const beforeMs = currentWeekStart.getTime();

  const fetchPage = useCallback(
    (before: number, limit = pageSize + 1) =>
      apiList<TimeEntry & { id: string }>('/timeEntries', {
        userId,
        before,
        limit,
      }),
    [userId, pageSize],
  );

  useEffect(() => {
    if (!userId) {
      setEntries([]); setLoading(false); setHasMore(false); cursorRef.current = null;
      return;
    }
    let cancelled = false;
    setLoading(true); setEntries([]); setHasMore(false); setError(null);
    cursorRef.current = null; inFlightRef.current = false;
    fetchPage(beforeMs)
      .then((rows) => {
        if (cancelled) return;
        const page = rows.slice(0, pageSize);
        cursorRef.current = page.length ? tsToMillis(page[page.length - 1].startTime) : null;
        setHasMore(rows.length > pageSize);
        setEntries(page.map((r) => ({ id: r.id, data: r })));
        setLoading(false);
      })
      .catch((e) => { if (!cancelled) { setError(e as FirestoreError); setLoading(false); } });
    return () => { cancelled = true; };
  }, [userId, beforeMs, pageSize, fetchPage]);

  // Same gap as `useProjectEntriesPaged`: accumulated pages, no refresh path.
  // Re-read the loaded window on any write so an edited/deleted history entry
  // updates in place without losing the scroll position.
  const loadedCountRef = useRef(0);
  loadedCountRef.current = entries.length;
  useEffect(() => {
    if (!userId) return;
    return onMutation(() => {
      const size = loadedCountRef.current;
      if (!size) return;
      fetchPage(beforeMs, size + 1)
        .then((rows) => {
          const page = rows.slice(0, size);
          cursorRef.current = page.length ? tsToMillis(page[page.length - 1].startTime) : null;
          setHasMore(rows.length > size);
          setEntries(page.map((r) => ({ id: r.id, data: r })));
        })
        .catch((e) => setError(e as FirestoreError));
    });
  }, [userId, beforeMs, fetchPage]);

  const loadMore = useCallback(() => {
    if (inFlightRef.current || !hasMore || cursorRef.current == null) return;
    inFlightRef.current = true;
    setLoadingMore(true);
    // ponytail: ms cursor (not a snapshot) — same boundary-tie caveat as the paged hook.
    fetchPage(cursorRef.current)
      .then((rows) => {
        const page = rows.slice(0, pageSize);
        if (page.length) cursorRef.current = tsToMillis(page[page.length - 1].startTime);
        setHasMore(rows.length > pageSize);
        setEntries((prev) => [...prev, ...page.map((r) => ({ id: r.id, data: r }))]);
        setLoadingMore(false); inFlightRef.current = false;
      })
      .catch((e) => { setError(e as FirestoreError); setLoadingMore(false); inFlightRef.current = false; });
  }, [hasMore, fetchPage, pageSize]);

  return { entries, loading, loadingMore, hasMore, error, loadMore };
}
